import * as Constants from "./../Constants";

import {
	AlignmentType,
	EcsResponseBase,
	IAppGovernanceProvider,
	ICVSurveyParams,
	ICampaignDefinition,
	ICampaignTelemetrySamplingConfig,
	IEcsAdapter,
	IFloodgateAuthTokenCallback,
	IFloodgateFeatureStatusCallback,
	IFloodgateSettingStorageCallback,
	ILogExperimentTrigger,
	IOnSurveyActivatedCallback,
	IUxSchemaVariables
} from "@ms-ofb/floodgate-shared-typings";
import { IManifestDataApplication, IManifestDataTelemetry, IManifestDataWeb } from "./../Transport/Transport";

import { TelemetrySink } from "@microsoft/oteljs";
import { IPersonalizationSettings, IUserFactConfig } from "@ms-ofb/officepersonalization/dist/lib/Common/Models";
import { IOnDismissDelegate } from "./../IOnDismissDelegate";
import { ISurveyCSSParams } from "./ISurveyCSSParams";

/**
 * Interface for common initialization options
 */
export interface IInitOptionsCommon {
	// An id uniquely identifying the host app in the OCV world
	appId: number;
	// The Otel.TelemetrySink implementing object that the logger will attach to.
	telemetrySink: TelemetrySink;
	// The url at which the styles reside
	stylesUrl?: string;
	// The url at which the internationalization files reside
	intlUrl?: string;
	// The filename for the internationalization strings, if different than the default filename
	intlFilename?: string;
	// Base url where any ux assets reside. Mainly used by coaching ux
	webSurfacesResourceBaseUrl?: string;
	// Environment
	environment: Constants.Environment;
	// A string uniquely identifying the current session
	sessionId?: string;
	// cid string which is in-scope for GDPR compliance for export and deletion request
	cid?: string;
	// A build version for your app
	build?: string;
	// The primary colour in hex form #[0-9a-f]{6}
	primaryColour?: string;
	// The secondary colour in hex form #[0-9a-f]{6}
	secondaryColour?: string;
	// The UI locale name of the calling page
	locale?: string;
	// System-Use only
	originalLocale?: string;
	// A callback which will be called when the SDK encounters errors
	onError?: (err: string) => void;
	// Application related metadata
	applicationGroup?: IManifestDataApplication;
	// Telemetry related metadata
	telemetryGroup?: IManifestDataTelemetry;
	// Web related metadata
	webGroup?: IManifestDataWeb;
	// User Email 
	userEmail?: string;
	// The json string to define event sampling
	eventSampling?: ISamplingEvent[];
	// The timeout for sending response in petrol
	petrolTimeout?: number;
	// check to see if user is consumer/commercial to dynamically display UI
	isCommercialHost?: boolean;
	// Option to see if the host will be providing their own resources such as strings or CSS
	customResourcesSetExternally?: CustomResources;
	// the email policy value from OCPS to determines if the user can see an option to include their email when they submit feedback
	emailPolicyValue?: Constants.OCPSValues;
	// the screenshot policy value from OCPS to determines if the user can see an option to include their screenshot when they submit feedback
	screenshotPolicyValue?: Constants.OCPSValues;
	// AgeGroup enum which user is classfied as
	ageGroup?: AgeGroup;
	// Authentication Type of user
	authenticationType?: AuthenticationType;
	// Application settings
	applicationSettings?: IApplicationSettings;
	// Package settings for the new web surfaces urls (after migration of the CDN)
	webSurfacesSettings?: IWebSurfacesSettings;
	// Privacy Url
	privacyUrl?: string;
	// Retention Duration in Days 
	retentionDurationDays?: number;
	// Config options for UserFacts
	userfactConfig?: IUserFactConfig;
	// Config for Personalization
	personalizationSettings?: IPersonalizationSettings;
	// Allowed domains script injection (Trusted types)
	allowedDomains?: string[];
}

/**
 * Interface for messaging service settings
 */
export interface IMessagingServiceSettings {
	// Internal campaign metadata aggregator url
	campaignMetadataAggregatorInt?: string;
	// Production campaign metadata aggregator url 
	campaignMetadataAggregatorProd?: string;
	// Internal set campaign action url
	setCampaignActionInt?: string;
	// Production set campaign action url
	setCampaignActionProd?: string;
}

/**
 * Interface for package settings
 */
export interface IWebSurfacesSettings {
	// Internal web surfaces url
	webSurfacesIntUrl?: string;
	// Production web surfaces url 
	webSurfacesProdUrl?: string;
}

/**
 * Interface for host application settings
 */
export interface IApplicationSettings {
	readonly [key: string]: unknown;
}

/**
 * Interface for inAppFeedback initialization options
 */
export interface IInitOptionsInAppFeedback {
	// Method called on feedback submission
	onDismiss?: IOnDismissDelegate;
	// Bug form toggle
	bugForm?: boolean;
	// email enabled
	showEmailAddress?: boolean;
	// User email
	userEmail?: string;
	// Screenshot toggle
	screenshot?: boolean;
	// The switch to show a thank you panel
	isShowThanks?: boolean;
	// Flag to enable/disable transistion on the UI.
	transitionEnabled?: boolean;
	// the feedback policy value from OCPS to determines if the user can submit feedback
	sendFeedbackPolicyValue?: Constants.OCPSValues;
	// Feedback Forum URL
	feedbackForumUrl?: string;
	// My feedback Forum URL
	myFeedbackForumUrl?: string;
}

/**
 * Interface for floodgate initialization options
 */
export interface IInitOptionsFloodgate {
	// Should the floodgate prompt automatically dismiss if not clicked? Default is false
	autoDismiss?: Constants.AutoDismissValues;

	// Campaign definitions
	campaignDefinitions?: ICampaignDefinition[];

	// Flights for TMS service
	campaignFlights?: string;

	// Additional query parameters for TMS service
	campaignQueryParameters?: string;

	// Delegate to be called when the floodgate UI is dismissed, whether on a submission or not
	onDismiss?: (campiagnId: string, submitted: boolean) => void;

	// The callback to be executed when a survey is activated
	onSurveyActivatedCallback?: IOnSurveyActivatedCallback;

	// The callback to be executed when host-based data operations are needed
	settingStorageCallback?: IFloodgateSettingStorageCallback;

	// Optionally provide a method which will accept the custom strings in Campaign Definitions and return actual
	// strings that must be displayed. Used for localization.
	uIStringGetter?: (str: string) => string;

	// The callback to be executed when host-based auth token is needed
	authTokenCallback?: IFloodgateAuthTokenCallback;

	// Calback for retrieving feature gate values from host app to enable experimental features 
	featureStatusCallback?: IFloodgateFeatureStatusCallback;

	// The boolean option to enforce if surveys are allowed by a policy, such as an OCPS policy for a tenant.
	// True: Surveys are enabled, floodgate works normally; False: Surveys are disabled
	surveyEnabled?: boolean;

	// Used to determine whether or LPP campaigns should be available to a user.
	// This is used to check the eligibility of a campaign prior to the nomination process.
	// Note: messagingEnabled and SurveyEnabled fall under the same "enabled types" for what type of surface a user can view.
	messagingEnabled?: boolean;

	// The boolean option to enforce if email address collection in a survey is allowed by a policy, such as an OCPS policy for a tenant.
	// True: email address collection is enabled, floodgate works normally; False: email address collection is disabled
	showEmailAddress?: boolean;

	// Interface to pass the params for the Customer Voice Survey
	customerVoiceSurveyParams?: ICVSurveyParamsExtended;

	// Interface for CSS params to modify survey position
	surveyCSSParams?: ISurveyCSSParams;

	// The boolean option to enabl the governance service
	governanceServiceEnabled?: boolean;

	// configuration for governance service
	governanceServiceConfig?: IGovernanceServiceConfig;

	// is personalizer enabled on client side.
	personalizerEnabled?: boolean;

	// Messaging service settings for the new base urls (after migration of the CDN)
	messagingServiceSettings?: IMessagingServiceSettings;

	// is priority filter enabled on client side.
	isPriorityEnabled?: boolean;

	// is EUDB compliant UX enabled on client side.
	isEudbUxEnabled?: boolean;

	// The App provided callback to be executed when app governance needs to be applied
	appGovernanceProvider?: IAppGovernanceProvider;

	// Configuration for Dynamic Campaigns
	dynamicCampaignConfig?: IDynamicCampaignConfig;

	// Configuration for Personalization package
	userfactConfig?: IUserFactConfig;

	// Callback for triggering LPP Scorecards
	logExperimentTrigger?: ILogExperimentTrigger;

	// setting to check if user facts Read service is enabled
	userFactsReadConfig?: IUserFactsReadConfig;

	// settings for dynamic ux
	dynamicUxConfig?: IDynamicUxConfig;

	// Sampling rate config for web funnel campaign feature, may include sampling rates for other campaign telemetry in the future
	campaignTelemetrySamplingConfig?: ICampaignTelemetrySamplingConfig;

	// HtmlElement on which the survey should be rendered. It can either be a string (elementId) or an HTMLElement. The element is created if it does not exist.
	surveyContainer?: string | HTMLElement;
}

/**
 * Interface for Dynamic Campaign options
 */
export interface IDynamicCampaignConfig {
	enabled: boolean;
	ecsClient?: IEcsAdapter<unknown & EcsResponseBase>;
}

/**
 * Interface for Dynamic Ux options
 */
export interface IDynamicUxConfig {
	enabled?: boolean;
	dynamicVariables?: IUxSchemaVariables;
	delayLoadingTimeout?: number;
	theme?: string;
	customTheme?: object;
	cdnStringConfig?: IDynamicUxCdnConfig;
	hostName?: string;
	footerButtonsAlignment?: AlignmentType;
}

/**
 * Type for Dynamic Ux theme options
 */
export type IDynamicUxThemeConfig = Pick<IDynamicUxConfig, "theme" | "customTheme">;

/**
 * Interface for Dynamic Ux CDN string options.
 * It defines how the CDN will be fetched: concurrently or sequentially with timeout and retry
 */
export interface IDynamicUxCdnConfig {
	uiStringsCdnEnabled: boolean;
	fetchCdnConcurrently?: boolean;
	uiStringsLoadingTimeout?: number;
	uiStringCdnRetryLimit?: number;
}

/**
 * Interface for floodgate event sampling options
 */
export interface ISamplingEvent {
	type: string;
	name: string;
	sampleRate: number;
}

export interface ISamplingInitOption {
	event?: Record<string, number>;
}

/**
 * Interface for User facts Read service settings
 */
export interface IUserFactsReadConfig {
	enabled: boolean;
}

export interface IGovernanceServiceConfig {
	// limit number of retry
	retry?: number;

	// timeout in second
	timeout?: number;

	// In case of failure call, option to whether continue display survey or fall off
	forceServicelessSurveyDisplay?: boolean;

	// Toggle the survey level governance
	isSurveyLevelGovernanceEnabled?: boolean;
}

// bit mask flag: values should be set 0,1,2,4,8,16, etc...
// Example for Css and Strings both, use 3.
export enum CustomResources {
	None = 0,
	Css = 1,
	Strings = 2,
}

export enum AgeGroup {
	Undefined, // Unknown age
	MinorWithoutParentalConsent, // Minor under the age of consent
	MinorWithParentalConsent, // Minor under age of consent
	Adult, // Adult
	NotAdult, // Minor above age of consent	
	MinorNoParentalConsentRequired, // Minor above age of consent
}

export enum AuthenticationType {
	MSA,
	AAD,
	Unauthenticated
}

// Taken from %SRCROOT%\privacy\options\public\UserConsentGroup.h
// Enum that describes the different consent categories of the user.
export const enum UserConsentGroup {
	// Unknown consent group, we do not have info to determine this
	Unknown = 0,
	// Can give consent to data collection, upsell messages
	CanConsent = 1,
	// Can still give consent but is only a limited consent.
	// As an example, upsell / cross sell should not be shown.
	PartialConsent = 2,
	// They cannot provide any form of consent. No consent dialogs / upsell should not be shown.
	CannotConsent = 3,
}

// TODO: Clean this code as part of the Migration of the CV surveys to FG 2.0. This code will be deprecated.
export interface ICVSurveyParamsExtended extends ICVSurveyParams {
	isCopilotLicenseEnabled?: boolean;
}